import TextField from "../ui/TextField";
import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, LinearProgress, List, ListItem, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncIcon from "@mui/icons-material/Sync";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useTenantSync } from "../../context/TenantSync/TenantSyncContext";

const terminalStatuses = new Set(["COMPLETED", "PARTIAL", "FAILED"]);

export default function TenantSyncModal({ open, onClose, syncType, itemKeys = [], title }) {
    const { relations, job, setJob, loadRelations, createRelation, revokeRelation, createJob, refreshJob } = useTenantSync();
    const [selected, setSelected] = useState([]);
    const [showRelationForm, setShowRelationForm] = useState(false);
    const [licenseCode, setLicenseCode] = useState("");
    const [alias, setAlias] = useState("");
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);

    const allowedRelations = useMemo(() => relations.filter((relation) =>
        syncType === "ARTICLES" ? relation.canSyncArticles : relation.canSyncCosts), [relations, syncType]);
    const running = job && !terminalStatuses.has(job.status);

    useEffect(() => {
        if (!open) return;
        setMessage(null);
        setJob(null);
        loadRelations();
    }, [open, loadRelations, setJob]);

    useEffect(() => {
        if (!open || !running || !job?.tenantSyncJobId) return undefined;
        const timer = window.setInterval(() => refreshJob(job.tenantSyncJobId), 900);
        return () => window.clearInterval(timer);
    }, [open, running, job?.tenantSyncJobId, refreshJob]);

    const toggleRelation = (relationId) => setSelected((current) =>
        current.includes(relationId) ? current.filter((id) => id !== relationId) : [...current, relationId]);

    const handleCreateRelation = async () => {
        setBusy(true);
        const response = await createRelation({
            licenseCode: licenseCode.trim().toUpperCase(), alias: alias.trim(),
            canSyncArticles: true, canSyncCosts: true
        });
        setBusy(false);
        if (response.hasError) return setMessage({ type: "error", text: response.message });
        setLicenseCode(""); setAlias(""); setShowRelationForm(false);
        setMessage({ type: "success", text: "La tienda quedó ligada de forma segura." });
    };

    const handleStart = async () => {
        if (selected.length === 0 || itemKeys.length === 0) return;
        setBusy(true); setMessage(null);
        const response = await createJob({ syncType, relationIds: selected, itemKeys });
        setBusy(false);
        if (response.hasError) setMessage({ type: "error", text: response.message });
    };

    const handleRevoke = async (relationId) => {
        if (!window.confirm("Se revocará esta liga. Esta acción no elimina datos de ninguna tienda.")) return;
        const response = await revokeRelation(relationId);
        if (response.hasError) setMessage({ type: "error", text: response.message });
        setSelected((current) => current.filter((id) => id !== relationId));
    };

    return (
        <Dialog open={open} onClose={running ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <SyncIcon color="primary" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{title || "Sincronizar tiendas"}</Typography>
                    <Typography variant="body2" color="text.secondary">{itemKeys.length} registros preparados</Typography>
                </Box>
                <Tooltip title="Cerrar"><span><IconButton onClick={onClose} disabled={running}><CloseIcon /></IconButton></span></Tooltip>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={2.5}>
                    {message && <Alert severity={message.type}>{message.text}</Alert>}
                    {job ? (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1" fontWeight={700}>Progreso de sincronizacion</Typography>
                                <Typography variant="body2">{Number(job.progressPercent || 0).toFixed(0)}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={Number(job.progressPercent || 0)} sx={{ height: 10, borderRadius: 1 }} />
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                {job.currentItem ? `Procesando ${job.currentItem}` : job.safeMessage || "Preparando operacion..."}
                            </Typography>
                            <List dense disablePadding sx={{ mt: 2 }}>
                                {(job.targets || []).map((target) => (
                                    <ListItem key={target.tenantRelationId} divider>
                                        <ListItemText primary={target.alias} secondary={`${target.status} | ${target.processedItems} procesados | ${target.createdItems} creados | ${target.updatedItems} actualizados`} />
                                    </ListItem>
                                ))}
                            </List>
                            {job.status === "PARTIAL" && <Alert severity="warning" sx={{ mt: 2 }}>La operación terminó con incidencias en una o més tiendas.</Alert>}
                            {job.status === "COMPLETED" && <Alert severity="success" sx={{ mt: 2 }}>Sincronizacion completada correctamente.</Alert>}
                            {job.status === "FAILED" && <Alert severity="error" sx={{ mt: 2 }}>No fue posible completar la sincronizacion.</Alert>}
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Tiendas enlazadas</Typography>
                                    <Typography variant="body2" color="text.secondary">Selecciona los destinos que recibiran esta informacion.</Typography>
                                </Box>
                                <Button startIcon={<AddLinkIcon />} onClick={() => setShowRelationForm((value) => !value)}>Nueva liga</Button>
                            </Box>
                            {showRelationForm && (
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" }, gap: 1.5, alignItems: "start" }}>
                                    <TextField autoComplete="off" label="Licencia de la otra tienda" value={licenseCode} onChange={(event) => setLicenseCode(event.target.value.toUpperCase())} size="small" />
                                    <TextField autoComplete="off" label="Nombre de la liga" value={alias} onChange={(event) => setAlias(event.target.value)} size="small" />
                                    <Button variant="contained" onClick={handleCreateRelation} disabled={busy || !licenseCode.trim() || !alias.trim()}>Ligar</Button>
                                </Box>
                            )}
                            {allowedRelations.length === 0 ? (
                                <Alert severity="info">Aun no hay tiendas ligadas para esta operacion.</Alert>
                            ) : (
                                <List disablePadding sx={{ border: "1px solid", borderColor: "divider" }}>
                                    {allowedRelations.map((relation) => (
                                        <ListItem key={relation.tenantRelationId} divider secondaryAction={
                                            <Tooltip title="Revocar liga"><IconButton edge="end" onClick={() => handleRevoke(relation.tenantRelationId)}><DeleteOutlineIcon /></IconButton></Tooltip>
                                        }>
                                            <FormControlLabel
                                                control={<Checkbox checked={selected.includes(relation.tenantRelationId)} onChange={() => toggleRelation(relation.tenantRelationId)} />}
                                                label={<Stack direction="row" spacing={1.5} alignItems="center"><StorefrontIcon color="action" /><Box><Typography fontWeight={700}>{relation.alias}</Typography><Typography variant="caption" color="text.secondary">{relation.targetTenantName} | {relation.targetDatabaseName}</Typography></Box></Stack>}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={running}>Cerrar</Button>
                {!job && <Button variant="contained" startIcon={<SyncIcon />} onClick={handleStart} disabled={busy || selected.length === 0 || itemKeys.length === 0}>Iniciar sincronizacion</Button>}
            </DialogActions>
        </Dialog>
    );
}
